import React from 'react';
import { Chip } from '@mui/material';

export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const errMsg = (e: any): string => {
  const d = e?.response?.data;
  if (d?.error) {
    const extra = d.errors ? `: ${d.errors.slice(0, 5).join('; ')}` : d.items ? `: ${d.items.slice(0, 10).join(', ')}` : '';
    return `${d.error}${extra}`;
  }
  return e?.message || 'Something went wrong';
};

export const num = (v: any, digits = 2): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-IN', { maximumFractionDigits: digits });
};

export const inr = (v: any): string => {
  const n = Number(v);
  if (v === null || v === undefined || !Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

export const isoDaysAgo = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

export const fmtDateTime = (v: any): string =>
  v ? new Date(v).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
  draft: 'default',
  reviewed: 'info',
  distributors_assigned: 'secondary',
  po_generated: 'primary',
  closed: 'success',
  cancelled: 'error',
  sent: 'primary',
  partially_received: 'warning',
  received: 'success',
  pending: 'default',
  partial: 'warning',
  not_supplied: 'error',
  accepted: 'success',
  rejected: 'error',
};

export const StatusChip: React.FC<{ status: string }> = ({ status }) => (
  <Chip size="small" label={String(status || '').replace(/_/g, ' ')} color={STATUS_COLORS[status] || 'default'} />
);

export const RUN_STATUS_LABEL: Record<string, string> = {
  draft: 'Review items',
  reviewed: 'Assign distributors',
  distributors_assigned: 'Check distributors & generate POs',
  po_generated: 'POs generated',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

export const PO_NEXT: Record<string, string[]> = {
  draft: ['sent', 'cancelled'],
  sent: ['partially_received', 'received', 'closed', 'cancelled'],
  partially_received: ['received', 'closed'],
  received: ['closed'],
  closed: [],
  cancelled: ['draft'],
};

export const EVENT_LABEL: Record<string, string> = {
  run_created: 'Run created',
  status_changed: 'Run status',
  item_decided: 'Item accepted/rejected',
  qty_changed: 'Quantity edited',
  item_added: 'Item added',
  distributors_assigned: 'Distributors assigned',
  distributor_overridden: 'Distributor changed',
  po_created: 'PO created',
  po_status_changed: 'PO status',
  receipt_matched: 'Receipt matched',
  receipt_override: 'Receipt edited',
  customer_orders_marked_ordered: 'Customer orders → ordered',
  line_reordered: 'Re-ordered from another distributor',
  customer_orders_added: 'Customer orders added',
  items_reset_to_pending: 'Items reset to pending',
};

/** Same rule as the PO (po_request.py extract_pack_count): last number, only for tablet/capsule/piece/lozenge/…s packs. */
export const packCount = (packSize?: string | null): number | null => {
  if (!packSize || typeof packSize !== 'string') return null;
  if (!/(tablet|capsule|piece|lozenge|s\b)/.test(packSize.toLowerCase())) return null;
  const nums = packSize.match(/\d+/g);
  return nums ? parseInt(nums[nums.length - 1], 10) : null;
};

/** Strips the PO will carry for a loose quantity (ceil), or null when the pack has no count (ml/gm → loose qty). */
export const stripsFor = (looseQty: any, packSize?: string | null): number | null => {
  const q = Number(looseQty);
  const c = packCount(packSize);
  return Number.isFinite(q) && c && c > 0 ? Math.ceil(q / c) : null;
};
